import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import {
    ShieldAlert,
    ShieldCheck,
    Lock,
    EyeOff,
    Loader2,
    LayoutDashboard,
    RotateCcw,
    FileText,
    BrainCircuit,
    ChevronRight,
    Scale,
    AlertCircle,
    FileWarning,
    History,
    Target,
    UserCheck,
    Shield
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { UsageTracker } from "@/lib/services/usage-tracker";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WhistleblowerResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    risk_assessment?: {
        severity: "low" | "medium" | "high" | "critical";
        impact_areas: string[];
        policy_violations: string[];
    };
    report_analysis?: string;
    protection_measures?: Array<{
        title: string;
        description: string;
        legal_basis: string;
    }>;
    suggested_steps?: Array<{
        step: number;
        action: string;
        responsible: string;
    }>;
}

interface WhistleblowerForm {
    report_type: string;
    incident_date: string;
    incident_description: string;
    involved_parties: string;
    evidence_description: string;
    is_anonymous: boolean;
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .wv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
        min-height: 100vh;
      }

      .wv2-header {
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #334155 100%);
        padding: 2rem 2.5rem;
        position: relative;
        overflow: hidden;
      }
      .wv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 80%, rgba(203,213,225,0.1) 0%, transparent 60%);
      }
      .wv2-header-content { position: relative; z-index: 1; }

      .wv2-body { display: flex; min-height: calc(100vh - 140px); }
      .wv2-form-panel {
        width: 35%;
        min-width: 360px;
        background: #fff;
        border-right: 1px solid #e2e8f0;
        padding: 2rem;
        overflow-y: auto;
      }
      .wv2-analytics-panel { flex: 1; padding: 2rem; overflow-y: auto; }

      @media (max-width: 768px) {
        .wv2-header {
          padding: 1.25rem 1rem !important;
        }
        .wv2-body {
          flex-direction: column !important;
          min-height: auto !important;
        }
        .wv2-form-panel {
          width: 100% !important;
          min-width: 100% !important;
          border-right: none !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 1.25rem !important;
        }
        .wv2-analytics-panel {
          width: 100% !important;
          padding: 1.25rem !important;
        }
      }

      .wv2-field-group { margin-bottom: 1.5rem; animation: slideUp 0.4s ease-out; }
      .wv2-label {
        font-size: 0.7rem;
        font-weight: 900;
        color: #1e293b;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }

      .wv2-card {
        background: #fff;
        border: 1px solid #f1f5f9;
        border-radius: 1.5rem;
        padding: 1.5rem;
        box-shadow: 0 4px 20px -5px rgba(15,23,42,0.05);
      }

      @keyframes slideUp { 
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function WhistleblowerV2() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startWhistleblowerRun, getRun } = useAgentExecution();
    const isMobile = useIsMobile();
    const [mobileTab, setMobileTab] = useState<"form" | "results">("form");

    const [form, setForm] = useState<WhistleblowerForm>({
        report_type: "Ética y Conducta",
        incident_date: new Date().toISOString().split('T')[0],
        incident_description: "",
        involved_parties: "",
        evidence_description: "",
        is_anonymous: true
    });

    const runId = "whistleblower-run";
    const currentRun = getRun(runId);
    const loading = currentRun?.status === "loading";
    const result = currentRun?.metadata as WhistleblowerResult;

    const handleRunAnalysis = async () => {
        if (!form.incident_description.trim()) {
            toast.error("Por favor, describa el incidente.");
            return;
        }

        // Track usage for dashboard
        if (user) {
            UsageTracker.trackWhistleblower(user.id, "Reporte enviado", form.report_type, undefined);
        }

        if (isMobile) {
            setMobileTab("results");
        }

        await startWhistleblowerRun(runId, form, apiClient, user?.id || null);
    };

    return (
        <div className="wv2-root">
            <PremiumStyles />

            <div className="wv2-header">
                <div className="wv2-header-content">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20">
                                <ShieldAlert className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-black text-white tracking-tighter uppercase italic">Protección al Denunciante V2</h1>
                                <p className="text-slate-400 text-[10px] font-bold tracking-widest uppercase opacity-70">Ethical Compliance & Safety Engine</p>
                            </div>
                        </div>
                        <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/dashboard")}>
                            <LayoutDashboard className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            {isMobile && (result || loading) && (
                <div className="bg-white border-b border-slate-200 p-2 sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setMobileTab("form")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                mobileTab === "form"
                                    ? "bg-white text-slate-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            📝 Denuncia
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab("results")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                mobileTab === "results"
                                    ? "bg-white text-slate-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <ShieldAlert className="h-3.5 w-3.5" />
                            <span>Análisis & Riesgo</span>
                            {result?.risk_assessment?.severity ? (
                                <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-black uppercase">
                                    {result.risk_assessment.severity}
                                </span>
                            ) : loading ? (
                                <Loader2 className="h-3 w-3 animate-spin text-slate-600" />
                            ) : null}
                        </button>
                    </div>
                </div>
            )}

            <div className="wv2-body">
                {(!isMobile || !result || mobileTab === "form") && (
                <div className="wv2-form-panel">
                    <div className="space-y-6">
                        <div className="wv2-field-group">
                            <div className="wv2-label"><FileWarning className="h-4 w-4" /> Tipo de Reporte</div>
                            <Select value={form.report_type} onValueChange={(v) => setForm(f => ({ ...f, report_type: v }))}>
                                <SelectTrigger className="border-slate-200 rounded-xl">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Ética y Conducta">Ética y Conducta</SelectItem>
                                    <SelectItem value="Fraude Financiero">Fraude Financiero</SelectItem>
                                    <SelectItem value="Acoso Laboral">Acoso Laboral</SelectItem>
                                    <SelectItem value="Conflictos de Interés">Conflictos de Interés</SelectItem>
                                    <SelectItem value="Seguridad de Información">Seguridad de Información</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="wv2-field-group">
                            <div className="wv2-label"><Target className="h-4 w-4" /> Descripción del Incidente</div>
                            <Textarea
                                value={form.incident_description}
                                onChange={(e) => setForm(f => ({ ...f, incident_description: e.target.value }))}
                                placeholder="Describa detalladamente lo sucedido..."
                                className="border-slate-200 min-h-[150px] rounded-xl text-sm"
                            />
                        </div>

                        <div className="wv2-field-group">
                            <div className="wv2-label"><UserCheck className="h-4 w-4" /> Partes Involucradas</div>
                            <Input
                                value={form.involved_parties}
                                onChange={(e) => setForm(f => ({ ...f, involved_parties: e.target.value }))}
                                placeholder="Nombres o Roles"
                                className="border-slate-200 rounded-xl"
                            />
                        </div>

                        <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                            <div className={`p-2 rounded-full ${form.is_anonymous ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'}`}>
                                <EyeOff size={18} />
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-black uppercase text-slate-900">Modo Anónimo</p>
                                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter italic">Su identidad está protegida por encriptación avanzada</p>
                            </div>
                        </div>

                        <Button
                            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-black rounded-xl gap-2 shadow-xl shadow-slate-200"
                            onClick={handleRunAnalysis}
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                            Enviar Reporte AI
                        </Button>

                        {currentRun?.traceSteps && (
                            <div className="mt-8 space-y-3 px-2 border-t pt-6">
                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-4">Pipeline de Protección</h4>
                                {currentRun.traceSteps.map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
                                        <span className="text-lg">{s.emoji}</span>
                                        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">{s.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                )}

                {(!isMobile || ((result || loading) && mobileTab === "results")) && (
                <div className="wv2-analytics-panel">
                    {!result && !loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                                <Lock className="h-12 w-12 text-slate-300" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Canal Seguro de Denuncias</h2>
                            <p className="text-slate-400 mt-2 max-w-sm font-medium text-sm">
                                Analice reportes de forma privada. Nuestra IA evaluará los riesgos y propondrá medidas de protección inmediatas.
                            </p>
                        </div>
                    ) : result?.status === 'rejected' ? (
                        <div className="max-w-2xl mx-auto py-12">
                            <div className="wv2-card border-t-4 border-t-red-500 shadow-2xl">
                                <div className="flex items-start gap-6 mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center text-red-600">
                                        <AlertCircle className="h-9 w-9" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-2">Validación Fallida</h3>
                                        <p className="text-slate-500 font-medium leading-relaxed">
                                            "{result.guard_rail_details?.reason || result.message}"
                                        </p>
                                    </div>
                                </div>
                                <Button className="w-full bg-slate-900 text-white font-bold h-12 rounded-xl" onClick={() => window.location.reload()}>
                                    Nuevo Reporte
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-700">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Risk Assessment */}
                                <div className="wv2-card border-l-4 border-l-red-500 bg-red-50/10">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="font-black text-red-900 text-xs uppercase italic tracking-tighter">Evaluación de Severidad</h3>
                                        <Badge variant="destructive" className="font-black uppercase text-[10px] px-3 py-1 bg-red-600">
                                            {result?.risk_assessment?.severity || "Crítica"}
                                        </Badge>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Áreas de Impacto</p>
                                            <div className="flex flex-wrap gap-2">
                                                {result?.risk_assessment?.impact_areas?.map((a, i) => (
                                                    <span key={i} className="px-3 py-1 bg-white border border-red-100 text-red-700 text-[10px] font-bold rounded-lg uppercase">{a}</span>
                                                )) || <span className="text-xs text-slate-400">Analizando impacto...</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Protection Measures */}
                                <div className="wv2-card border-l-4 border-l-indigo-500">
                                    <h3 className="font-black text-indigo-900 text-xs uppercase italic mb-6">Medidas de Protección</h3>
                                    <div className="space-y-3">
                                        {result?.protection_measures?.map((m, i) => (
                                            <div key={i} className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-2 mb-1">
                                                    <Shield size={14} className="text-indigo-600" /> {m.title}
                                                </h4>
                                                <p className="text-[10px] text-slate-600 mb-1">{m.description}</p>
                                                <p className="text-[8px] font-black text-indigo-800 uppercase italic opacity-70">Base: {m.legal_basis}</p>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-4">Calculando salvaguardas legales...</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                    }
                </div>
                )}
            </div>
        </div>
    );
}
